import { QueryClient, QueryFunctionContext } from '@tanstack/react-query';
import { produce } from 'immer';
import { ChartsTheme } from '@/components/model/theme';
import { AppMetadataProps, DATA_QUERY_KEY, Notice, QueryDefinition, SupportedDataType, UnknownSpec } from '@/core';
import {
  DataQueryPlugin,
  GetDataResponse,
  InternalGetDataResponse,
  QueryPluginContext,
  QueryPluginGetDataBasicResponse,
} from '@/plugin-system/model';
import {
  DataQuery,
  getDataFunction,
  initializeDataQueryPlugin,
  isProgressiveDataQueryPlugin,
  ProgressiveDataQuery,
} from '../DataQueryPlugins';
import { PluginRegistryContextType } from '../plugin-registry';
import { normalizeDataResponseNotices } from './data-utils';
import { DataPluginQueryMethod, DataQueryPagination, UseDataQueryOptions } from './model';
import { processPluginDefinition } from './plugin-definition-processing';
import { cleanupProgressiveQuery, handleProgressiveQueryStopping } from './progressive-query-utils';
import { applyQueryThreshold } from './threshold';

type GetDataQueryQueryFnOpts = {
  chartsTheme: ChartsTheme;
  context: QueryPluginContext;
  definition: QueryDefinition;
  enablePagination: boolean;
  getPlugin: PluginRegistryContextType['getPlugin'];
  id: string;
  mapPageToNextCursor: React.MutableRefObject<string[]>;
  queryClient: QueryClient;
  queryMethod: DataPluginQueryMethod;
  appMetadata?: AppMetadataProps;
  cursorToFetch?: string;
  options?: UseDataQueryOptions;
  paginationModel?: DataQueryPagination;
};

/**
 * Returns a `queryFn` that `useDataQueries` will use to fetch and process results for the
 * specified plugin definition.
 */
export function getDataQueryQueryFn<T extends SupportedDataType>({
  appMetadata,
  chartsTheme,
  context,
  cursorToFetch,
  definition,
  enablePagination,
  getPlugin,
  id,
  mapPageToNextCursor,
  options,
  paginationModel,
  queryClient,
  queryMethod,
}: GetDataQueryQueryFnOpts) {
  return async ({ queryKey, signal }: QueryFunctionContext): Promise<GetDataResponse<T>> => {
    const pluginDefinition = { ...definition.spec.plugin, id };
    const pluginDefinitionKind = pluginDefinition.kind;

    // Keep options out of query key so we don't re-run queries because suggested step changes
    const ctx: QueryPluginContext = {
      ...context,
      alias: definition.spec.alias,
      suggestedStepMs: options?.suggestedStepMs,
      appMetadata,
      chartsTheme,
      pagination: enablePagination
        ? {
            cursor: cursorToFetch,
            pageSize: paginationModel?.pageSize,
          }
        : undefined,
    };
    const plugin = await getPlugin(DATA_QUERY_KEY, pluginDefinitionKind);
    const dataQueryPlugin = initializeDataQueryPlugin(plugin);

    const results = await processPluginDefinition({
      context,
      definition: pluginDefinition,
      plugin: dataQueryPlugin,
    });

    const processedPluginDefinitionSpec = results.definition.spec;
    const pluginNotices: Notice[] = results.notices;

    const queryFnHandlerOpts: QueryFnHandlerOpts = {
      chartsTheme,
      context: ctx,
      definition,
      enablePagination,
      mapPageToNextCursor,
      paginationModel,
      pluginNotices,
      processedPluginDefinitionSpec,
      queryClient,
      queryKey,
      queryMethod,
      signal,
    };

    if (isProgressiveDataQueryPlugin(dataQueryPlugin)) {
      return handleProgressiveDataQueryFn(dataQueryPlugin, queryFnHandlerOpts);
    }

    return handleBasicDataQueryFn(dataQueryPlugin, queryFnHandlerOpts);
  };
}

type QueryFnHandlerOpts = Pick<
  GetDataQueryQueryFnOpts,
  | 'chartsTheme'
  | 'context'
  | 'definition'
  | 'enablePagination'
  | 'mapPageToNextCursor'
  | 'paginationModel'
  | 'queryClient'
  | 'queryMethod'
> &
  Pick<QueryFunctionContext, 'signal' | 'queryKey'> & {
    pluginNotices: Notice[];
    processedPluginDefinitionSpec: UnknownSpec;
  };

/**
 * Handles queryFn logic specific to "basic" data querying. "Basic" data querying involves either an
 * object-based query plugin or a class-based query plugin that uses react-query's default
 * query handling, which expects a single asynchronous result.
 */
async function handleBasicDataQueryFn<T extends SupportedDataType, Plugin extends DataQueryPlugin | DataQuery>(
  dataQueryPlugin: Plugin,
  {
    chartsTheme,
    context,
    definition,
    enablePagination,
    mapPageToNextCursor,
    paginationModel,
    pluginNotices,
    processedPluginDefinitionSpec,
    queryMethod,
    signal,
  }: QueryFnHandlerOpts
): Promise<GetDataResponse<T>> {
  // Fall back to 'getData' method if queryMethod is not available on the plugin.
  // TODO: Some kind of error notification that this query type does not support the query method
  const getDataFn = getDataFunction<T>(dataQueryPlugin, { queryMethod, signal });
  const data = await getDataFn(processedPluginDefinitionSpec, context);

  const pagination = data.data.metadata?.pagination;
  if (enablePagination && pagination?.cursor && paginationModel?.page !== undefined) {
    mapPageToNextCursor.current[paginationModel.page] = pagination.cursor;
  }

  // Apply per-query threshold augmentation.
  const styledData = applyQueryThreshold(data, { definition, context, chartsTheme });

  return normalizeDataResponseNotices<T>({ data: styledData, pluginNotices });
}

/**
 * Handles queryFn logic specific to "progressive" data querying. "Progressive" data querying involves
 * a class-based progressive query plugin. This logic cannot rely on react-query's default query
 * handling because progressive loading is not a feature of react-query. Instead we build this
 * functionality on top of react-query using a combination of their default query handling (for
 * the first update) and `queryClient.setQueryData` for additional updates.
 */
async function handleProgressiveDataQueryFn<T extends SupportedDataType, Plugin extends ProgressiveDataQuery>(
  dataQueryPlugin: Plugin,
  {
    chartsTheme,
    context,
    definition,
    pluginNotices,
    processedPluginDefinitionSpec,
    queryClient,
    queryKey,
    queryMethod,
    signal,
  }: QueryFnHandlerOpts
): Promise<InternalGetDataResponse<T>> {
  const getDataFn = getDataFunction<T>(dataQueryPlugin, { queryMethod, signal });

  const { getProgressiveData } = await getDataFn(processedPluginDefinitionSpec, context);

  // Handle important use cases around cancellation and unmounting that aren't handled by react-query
  // because it does not natively support progressive loading.
  const unsubscribeQueryCache = handleProgressiveQueryStopping(dataQueryPlugin, {
    queryClient,
    queryKey,
  });

  let isFirstResultPromiseFulfilled = false;
  let resolveFirstResultPromise: (value: GetDataResponse<T> | PromiseLike<GetDataResponse<T>>) => void;
  let rejectFirstResultPromise: (reason?: unknown) => void;

  // React-query needs a promise for handling the first result in its "normal" way. Using this
  // promise to handle that use case. After that, we have to make updates via the queryClient
  // because react-query does not natively support progressive loading.
  const firstResultPromise = new Promise<GetDataResponse<T>>((resolve, reject) => {
    resolveFirstResultPromise = resolve;
    rejectFirstResultPromise = reject;
  });

  try {
    // Calling `getProgressiveData` tells the progressive query plugin to start the loading process.
    // It then will call the specified callbacks when it has new information, so we can make the
    // appropriate updates to react-query.
    await getProgressiveData({
      onUpdate: async (updatedDataOrUpdaterFn, opts) => {
        const isComplete = !!opts?.isComplete;
        if (isComplete) {
          cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache);
        }

        const getFormattedUpdatedData = (prevData: QueryPluginGetDataBasicResponse<T> | undefined) => {
          const updatedData =
            typeof updatedDataOrUpdaterFn === 'function' ? updatedDataOrUpdaterFn(prevData) : updatedDataOrUpdaterFn;

          // Apply per-query threshold augmentation per progressive frame so
          // the running response stays tagged/cleared as new data arrives.
          const styled = applyQueryThreshold(
            {
              ...updatedData,
              internalMetadata: {
                isFetchingMore: isComplete ? false : true,
              },
            },
            { definition, context, chartsTheme }
          );

          return normalizeDataResponseNotices<T>({ data: styled, pluginNotices });
        };

        if (!isFirstResultPromiseFulfilled) {
          isFirstResultPromiseFulfilled = true;

          // First update. Handle this the "normal" react-query way by resolving the promise that the
          // queryFn returns. Passing `undefined` because there is no previous data.
          resolveFirstResultPromise(getFormattedUpdatedData(undefined));
        } else {
          // Ensure first promise is completed before calling `setQueryData` to avoid results being
          // updated in the wrong order.
          await firstResultPromise;

          // Additional updates. Manually update the react-query data for this query using the
          // queryClient because react-query does not support progressive loading natively.
          queryClient.setQueryData(queryKey, (prevData: QueryPluginGetDataBasicResponse<T> | undefined) => {
            return getFormattedUpdatedData(prevData);
          });
        }
      },
      onComplete: async () => {
        cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache);

        if (!isFirstResultPromiseFulfilled) {
          isFirstResultPromiseFulfilled = true;

          // This function was called before any data was updated, so we throw a relatively generic
          // error because this is very likely a mistake in the plugin implementation, which is not
          // something we want to communicate to the user.
          rejectFirstResultPromise(
            new Error('Something went wrong fetching data. Refresh to try loading the results again.')
          );
        }

        // Ensure first promise is completed before calling `setQueryData` to avoid results being
        // updated in the wrong order.
        await firstResultPromise;

        // Done fetching data. Update the latest result to communicate the progressive loading is
        // completed.
        queryClient.setQueryData(queryKey, (prevData: InternalGetDataResponse | undefined) => {
          // Use produce here because you MUST not mutate the prevData that's in the react-query
          // cache.
          return produce(prevData, (draft) => {
            if (draft) {
              draft.internalMetadata = {
                isFetchingMore: false,
              };
            }
          });
        });
      },
      onError: async (e) => {
        cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache);

        if (!isFirstResultPromiseFulfilled) {
          isFirstResultPromiseFulfilled = true;

          // Error received before any updates were resolved. Handle this the "normal" react-query
          // way by rejecting the promise that the queryFn returns.
          rejectFirstResultPromise(e);
        } else {
          // Ensure first promise is completed before calling `setQueryData` to avoid results being
          // updated in the wrong order.
          await firstResultPromise;

          // Error called after the promise was resolved, so we cannot rely on react-query to handle
          // this the "normal" way. We manually update the react-query data to communicate there is
          // an error and that the progressive loading is done.
          queryClient.setQueryData(queryKey, (prevData: InternalGetDataResponse | undefined) => {
            return produce(prevData, (draft) => {
              if (draft) {
                draft.internalMetadata = {
                  additionalError: e,
                  isFetchingMore: false,
                };
              }
            });
          });
        }
      },
    });
  } catch (e) {
    // If call to `getProgressiveData` throws an error, make sure we clean up the
    // progressive query. Note that most other error handling needs to happen inside the plugin itself
    // and then call `onError` because with the use of promises and callback functions, this code
    // generally won't be able to catch those errors.
    cleanupProgressiveQuery(dataQueryPlugin, unsubscribeQueryCache);

    // Rethrow the error, so react-query uses it to set error state.
    throw e;
  }

  return firstResultPromise;
}
